import { Plus, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { Modal } from "@/components/Modal";
import type { FoodClarification } from "@/lib/schema";
import { suggestClarification } from "@/lib/suggestClarification";

interface Props {
  open: boolean;
  initial: FoodClarification[];
  onSave: (clarifications: FoodClarification[]) => void;
  onClose: () => void;
}

export function FoodClarificationsModal({
  open,
  initial,
  onSave,
  onClose,
}: Props) {
  if (!open) return null;
  return <ModalBody initial={initial} onSave={onSave} onClose={onClose} />;
}

function ModalBody({
  initial,
  onSave,
  onClose,
}: {
  initial: FoodClarification[];
  onSave: (clarifications: FoodClarification[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<FoodClarification[]>(initial);
  const [newText, setNewText] = useState("");
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const updateText = (id: string, text: string) => {
    setDraft((prev) => prev.map((f) => (f.id === id ? { ...f, text } : f)));
  };

  const remove = (id: string) => {
    setDraft((prev) => prev.filter((f) => f.id !== id));
  };

  const add = () => {
    const text = newText.trim();
    if (text.length === 0) return;
    setDraft((prev) => [...prev, { id: crypto.randomUUID(), text }]);
    setNewText("");
    setSuggestion(null);
    setSuggestError(null);
  };

  const suggest = async () => {
    const text = newText.trim();
    if (text.length === 0 || suggesting) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      setSuggestion(await suggestClarification(text));
    } catch (err) {
      setSuggestError(
        err instanceof Error ? err.message : "Forslag feilet. Prøv igjen.",
      );
    } finally {
      setSuggesting(false);
    }
  };

  const save = () => {
    // Blanked rows are dropped — an empty fact says nothing.
    onSave(
      draft
        .map((f) => ({ ...f, text: f.text.trim() }))
        .filter((f) => f.text.length > 0),
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="food-clarifications-modal"
      size="lg"
    >
      <h2>Matpresiseringer</h2>
      <p className="hint">
        Faste fakta KI-en bruker når du beskriver måltider, f.eks. «Smør på brød
        = 4 g Brelett per skive.» En konkret mengde eller et konkret produkt i
        samtalen vinner alltid over presiseringene.
      </p>

      {draft.length > 0 ? (
        <div className="task-list">
          {draft.map((fact) => (
            <div
              key={fact.id}
              className="task-row"
              data-testid={`clarification-row-${fact.id}`}
            >
              <input
                className="input"
                type="text"
                value={fact.text}
                onChange={(e) => updateText(fact.id, e.target.value)}
                aria-label="Matpresisering"
                data-testid={`clarification-text-input-${fact.id}`}
              />
              <button
                type="button"
                className="icon-btn-delete"
                onClick={() => remove(fact.id)}
                aria-label="Slett presisering"
                data-testid={`delete-clarification-button-${fact.id}`}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="hint" data-testid="clarifications-empty">
          Ingen presiseringer ennå.
        </p>
      )}

      <div className="task-list">
        <div className="task-row">
          <input
            className="input"
            type="text"
            placeholder="f.eks. knekkebrød = Wasa Husmann, 13 g per stk"
            value={newText}
            onChange={(e) => {
              setNewText(e.target.value);
              setSuggestion(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
            }}
            aria-label="Ny matpresisering"
            data-testid="new-clarification-input"
          />
          <button
            type="button"
            className="btn ghost icon-btn"
            disabled={newText.trim().length === 0 || suggesting}
            onClick={() => void suggest()}
            data-testid="suggest-formulation-button"
          >
            <Sparkles size={14} />
            {suggesting ? "Foreslår…" : "Foreslå formulering"}
          </button>
          <button
            type="button"
            className="btn ghost icon-btn"
            disabled={newText.trim().length === 0}
            onClick={add}
            data-testid="add-clarification-button"
          >
            <Plus size={14} />
            Legg til
          </button>
        </div>
        {suggestion !== null ? (
          <div className="task-row">
            <span className="hint" data-testid="suggestion-text">
              {suggestion}
            </span>
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setNewText(suggestion);
                setSuggestion(null);
              }}
              data-testid="use-suggestion-button"
            >
              Bruk forslag
            </button>
          </div>
        ) : null}
        {suggestError ? (
          <p className="field-error" data-testid="suggest-error">
            {suggestError}
          </p>
        ) : null}
      </div>

      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="clarifications-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn"
          onClick={save}
          data-testid="save-clarifications-button"
        >
          Lagre
        </button>
      </div>
    </Modal>
  );
}
